import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useShopProfileTranslation } from '../hooks/useShopProfileTranslation';
import api from '../config/api';
import { 
  ArrowLeft, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Coffee,
  Menu,
  Share2,
  Users,
  Instagram,
  Facebook,
  Twitter,
  Eye,
  Calendar,
  Image,
  X
} from 'lucide-react';
import { copyToClipboard } from '../utils/urlUtils';
import toast from 'react-hot-toast';
import PhotoLightbox from '../components/PhotoLightbox';
import BusinessHours from '../components/BusinessHours';
import { getProductImageUrl } from '../utils/imageUtils';

const ShopProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, rtlDirection } = useShopProfileTranslation();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [tables, setTables] = useState([]);
  const [posts, setPosts] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFullGallery, setShowFullGallery] = useState(false);
  const [showMenuDialog, setShowMenuDialog] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const fetchShopData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const shopResponse = await api.get(`/shop/${slug}`);
      const shopData = shopResponse.data;
      setShop(shopData);

      const [productsResponse, tablesResponse, postsResponse] = await Promise.all([
        api.get(`/shop/${slug}/products`),
        shopData.id ? api.get(`/shop/${shopData.id}/tables`) : Promise.resolve(null),
        api.get(`/shop/${slug}/posts`)
      ]);

      if (productsResponse.data) {
        setProducts(productsResponse.data);
      }

      if (tablesResponse && tablesResponse.data) {
        setTables(tablesResponse.data);
      }

      if (postsResponse.data) {
        const postsResponse_data = postsResponse.data;
        const postsData = postsResponse_data.success ? (postsResponse_data.data || []) : [];
        setPosts(postsData);
        
        const galleryImages = postsData
          .filter(post => post.images && post.images.length > 0)
          .flatMap(post => post.images)
          .slice(0, 12);
        setGalleryImages(galleryImages);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchShopData();
  }, [fetchShopData]);

  const handleShareShop = async () => {
    const url = window.location.origin + `/shop/${slug}`;
    const success = await copyToClipboard(url);
    if (success) {
      toast.success(t('shopLinkCopied'));
    } else {
      toast.error(t('failedToCopyShopLink'));
    }
  };

  const formatAddress = () => {
    const parts = [];
    if (shop?.address) parts.push(shop.address);
    if (shop?.city) parts.push(shop.city);
    if (shop?.postalCode) parts.push(shop.postalCode);
    if (shop?.country) parts.push(shop.country);
    return parts.join(', ') || t('noAddress');
  };

  const handleImageClick = (imageUrl, allImages) => {
    setLightboxImages(allImages);
    setCurrentImageIndex(allImages.indexOf(imageUrl));
    setShowLightbox(true);
  };

  const handleLightboxClose = () => {
    setShowLightbox(false);
    setLightboxImages([]);
    setCurrentImageIndex(0);
  };

  const handleImageIndexChange = (index) => {
    setCurrentImageIndex(index);
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4"></div>
          <p className="text-gray-300">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-3xl font-bold mb-4">{t('notFound')}</h1>
          <p className="text-gray-300 mb-6">{t('notFoundDescription')}</p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            {t('backToHome')}
          </button>
        </div>
      </div>
    );
  }

  const groupedProducts = products.reduce((groups, product) => {
    const category = product.category || 'OTHER';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(product);
    return groups;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827]" dir={rtlDirection}>
      {/* Header with Back Button */}
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-gray-400 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {t('backToHome')}
        </button>
      </div>

      {/* Hero Banner */}
      <div className="relative">
        {shop.bannerUrl ? (
          <div className="h-64 md:h-80 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${shop.bannerUrl})` }}>
            <div className="absolute inset-0 bg-black/40"></div>
          </div>
        ) : (
          <div className="h-64 md:h-80 bg-gradient-to-r from-[#a15507] to-[#924e06]"></div>
        )}
        
        {/* Profile Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-10">
          <div className="card shadow-2xl border border-gray-700/50">
            <div className="flex flex-col md:flex-row items-start md:items-end gap-6">
              {/* Logo */}
              <div className="flex-shrink-0">
                {shop.logoUrl ? (
                  <img 
                    src={shop.logoUrl} 
                    alt={shop.name}
                    className="w-28 h-28 md:w-36 md:h-36 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                ) : (
                  <div className="w-28 h-28 md:w-36 md:h-36 rounded-full bg-gradient-to-r from-[#a15507] to-[#924e06] flex items-center justify-center border-4 border-white shadow-lg">
                    <Coffee className="w-14 h-14 md:w-18 md:h-18 text-white" />
                  </div>
                )}
              </div>

              {/* Shop Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">{shop.name}</h1>
                    {shop.description && (
                      <p className="text-gray-300 text-lg mb-3">{shop.description}</p>
                    )}
                    
                    {/* Stats */}
                    <div className="flex items-center gap-4 text-gray-400 mb-4">
                      <div className="flex items-center gap-1">
                        <Coffee className="w-4 h-4" />
                        <span>{products.length} {t('products')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>{tables.length} {t('tables')}</span>
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm">
                      {shop.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          <span>{shop.phone}</span>
                        </div>
                      )}
                      {shop.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <span>{shop.email}</span>
                        </div>
                      )}
                      {formatAddress() !== t('noAddress') && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{formatAddress()}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <button
                      onClick={handleShareShop}
                      className="flex items-center justify-center px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl transition-all duration-200 text-white font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <Share2 className="w-5 h-5 mr-2" />
                      {t('share')}
                    </button>
                    <button
                      onClick={() => setShowMenuDialog(true)}
                      className="flex items-center justify-center px-8 py-3 bg-gradient-to-r from-[#a15507] to-[#924e06] hover:from-[#924e06] hover:to-[#7a3f05] rounded-xl transition-all duration-200 text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <Menu className="w-5 h-5 mr-2" />
                      {t('viewMenu')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Business Hours - Show on mobile first */}
            {shop.openingHours && (
              <div className="card shadow-xl border border-gray-700/50 lg:hidden">
                <BusinessHours 
                  openingHours={shop.openingHours} 
                  t={t}
                />
              </div>
            )}

            {/* About Section */}
            {shop.bio && (
              <div className="card">
                <h2 className="text-2xl font-bold text-white mb-4">{t('about')}</h2>
                <p className="text-gray-300 leading-relaxed">{shop.bio}</p>
              </div>
            )}

            {/* Specialties */}
            {shop.specialties && (
              <div className="card">
                <h2 className="text-2xl font-bold text-white mb-4">{t('specialties')}</h2>
                <div className="flex flex-wrap gap-2">
                  {shop.specialties.split(',').map((specialty, index) => (
                    <span key={index} className="px-3 py-1 bg-[#a15507] text-white rounded-full text-sm">
                      {specialty.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Posts Section */}
            <div className="card shadow-xl border border-gray-700/50">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">{t('recentPosts')}</h2>
                <div className="flex items-center gap-2 text-[#a15507] bg-[#a15507]/10 px-3 py-1 rounded-full">
                  <Coffee className="w-4 h-4" />
                  <span className="text-sm font-medium">{posts.length} {t('posts')}</span>
                </div>
              </div>
              
              {posts.length > 0 ? (
                <div className="space-y-6">
                  {posts.slice(0, 4).map((post) => (
                    <div key={post.id} className="bg-gradient-to-r from-gray-800/50 to-gray-700/30 rounded-xl p-6 border border-gray-600/30 hover:border-gray-500/50 transition-all duration-200 group">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#a15507] to-[#924e06] flex items-center justify-center shadow-lg flex-shrink-0">
                          <Coffee className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-white font-semibold text-lg">{shop.name}</h3>
                            <span className="text-gray-400 text-sm flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(post.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          {post.content && (
                            <p className="text-gray-300 leading-relaxed mb-4">{post.content}</p>
                          )}
                        </div>
                      </div>
                      
                      {post.images && post.images.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                          {post.images.slice(0, 6).map((image, index) => (
                            <div 
                              key={index} 
                              className="relative group/image overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer"
                              onClick={() => handleImageClick(image, post.images)}
                            >
                              <img
                                src={image}
                                alt={`Post ${index + 1}`}
                                className="w-full h-32 object-cover transition-transform duration-200 group-hover/image:scale-105"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover/image:opacity-100 transition-opacity duration-200">
                                <div className="absolute bottom-2 right-2">
                                  <Eye className="w-5 h-5 text-white drop-shadow-lg" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 text-gray-400 text-sm flex-wrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                        </div>
                        {post.location && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span>{post.location}</span>
                          </div>
                        )}
                        {post.category && (
                          <div className="px-2 py-1 bg-[#a15507] text-white text-xs rounded-full">
                            {post.category}
                          </div>
                        )}
                        {post.price && (
                          <div className="font-semibold text-green-400">
                            {post.price}
                          </div>
                        )}
                        {post.isFeatured && (
                          <div className="px-2 py-1 bg-yellow-500 text-black text-xs rounded-full font-bold">
                            Featured
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-gray-700 to-gray-600 flex items-center justify-center">
                    <Coffee className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-300 mb-2">{t('noPostsYet')}</h3>
                  <p className="text-gray-500">{t('noPostsDescription')}</p>
                </div>
              )}
            </div>

            {/* Image Gallery */}
            {galleryImages.length > 0 && (
              <div className="card shadow-xl border border-gray-700/50">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-white">{t('gallery')}</h2>
                    <div className="flex items-center gap-2 text-[#a15507] bg-[#a15507]/10 px-3 py-1 rounded-full">
                      <Image className="w-4 h-4" />
                      <span className="text-sm font-medium">{galleryImages.length}</span>
                    </div>
                  </div>
                  {galleryImages.length > 6 && (
                    <button
                      onClick={() => setShowFullGallery(!showFullGallery)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#a15507] hover:bg-[#924e06] text-white rounded-lg transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <Eye className="w-4 h-4" />
                      {showFullGallery ? t('showLess') : t('viewMore')}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {(showFullGallery ? galleryImages : galleryImages.slice(0, 6)).map((image, index) => (
                    <div 
                      key={index} 
                      className="relative group cursor-pointer overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                      onClick={() => handleImageClick(image, galleryImages)}
                    >
                      <img
                        src={image}
                        alt={`Gallery ${index + 1}`}
                        className="w-full h-28 md:h-36 object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-2 right-2">
                          <Eye className="w-6 h-6 text-white drop-shadow-lg" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* {t('menuPreview')} */}
            {Object.keys(groupedProducts).length > 0 && (
              <div className="card shadow-xl border border-gray-700/50">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">{t('menuPreview')}</h2>
                  <div className="flex items-center gap-2 text-[#a15507] bg-[#a15507]/10 px-3 py-1 rounded-full">
                    <Menu className="w-4 h-4" />
                    <span className="text-sm font-medium">{products.length} {t('products')}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(groupedProducts).slice(0, 4).map(([category, categoryProducts]) => (
                    <div key={category} className="space-y-4">
                      <h3 className="text-lg font-semibold text-white border-b border-gray-600 pb-3 flex items-center gap-2">
                        <Coffee className="w-5 h-5 text-[#a15507]" />
                        {t(`categories.${category.toLowerCase()}`, category)}
                      </h3>
                      {categoryProducts.slice(0, 3).map((product) => (
                        <div key={product.id} className="flex items-center gap-4 p-4 bg-gradient-to-r from-gray-800/50 to-gray-700/30 rounded-xl border border-gray-600/30 hover:border-gray-500/50 transition-all duration-200">
                          {product.imageUrl ? (
                            <img
                              src={getProductImageUrl(product.imageUrl)}
                              alt={product.name}
                              className="w-16 h-16 object-cover rounded-xl shadow-md"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-gradient-to-r from-[#a15507] to-[#924e06] rounded-xl flex items-center justify-center">
                              <Coffee className="w-8 h-8 text-white" />
                            </div>
                          )}
                          <div className="flex-1">
                            <h4 className="text-white font-semibold text-lg">{product.name}</h4>
                            {product.description && (
                              <p className="text-gray-400 text-sm mt-1 line-clamp-2">{product.description}</p>
                            )}
                          </div>
                          <div className="text-[#a15507] font-bold text-xl">
                            ${product.price?.toFixed(2) || '0.00'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Business Hours - Show on desktop only */}
            {shop.openingHours && (
              <div className="card shadow-xl border border-gray-700/50 hidden lg:block">
                <BusinessHours 
                  openingHours={shop.openingHours} 
                  t={t}
                />
              </div>
            )}

            {/* Social Media */}
            {(shop.website || shop.instagram || shop.facebook || shop.twitter) && (
              <div className="card shadow-xl border border-gray-700/50">
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-[#a15507]" />
                  {t('followUs')}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {shop.website && (
                    <a
                      href={shop.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center px-4 py-3 bg-gradient-to-r from-gray-700 to-gray-600 hover:from-gray-600 hover:to-gray-500 rounded-xl transition-all duration-200 text-white font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <Globe className="w-5 h-5 mr-2" />
                      <span className="text-sm">{t('website')}</span>
                    </a>
                  )}
                  {shop.instagram && (
                    <a
                      href={shop.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center px-4 py-3 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 rounded-xl transition-all duration-200 text-white font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <Instagram className="w-5 h-5 mr-2" />
                      <span className="text-sm">{t('instagram')}</span>
                    </a>
                  )}
                  {shop.facebook && (
                    <a
                      href={shop.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 rounded-xl transition-all duration-200 text-white font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <Facebook className="w-5 h-5 mr-2" />
                      <span className="text-sm">{t('facebook')}</span>
                    </a>
                  )}
                  {shop.twitter && (
                    <a
                      href={shop.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center px-4 py-3 bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-400 hover:to-sky-500 rounded-xl transition-all duration-200 text-white font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      <Twitter className="w-5 h-5 mr-2" />
                      <span className="text-sm">{t('twitter')}</span>
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Menu Dialog */}
      {showMenuDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-b from-[#1f2937] to-[#111827] rounded-2xl shadow-2xl border border-gray-700/50 max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-700/50">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <Coffee className="w-8 h-8 text-[#a15507]" />
                {shop?.name} Menu
              </h2>
              <button
                onClick={() => setShowMenuDialog(false)}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-gray-400 hover:text-white" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {Object.keys(groupedProducts).length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {Object.entries(groupedProducts).map(([category, categoryProducts]) => (
                    <div key={category} className="space-y-4">
                      <h3 className="text-xl font-semibold text-white border-b border-gray-600 pb-3 flex items-center gap-3">
                        <Coffee className="w-6 h-6 text-[#a15507]" />
                        {t(`categories.${category.toLowerCase()}`, category)}
                      </h3>
                      <div className="space-y-3">
                        {categoryProducts.map((product) => (
                          <div key={product.id} className="flex items-center gap-4 p-4 bg-gradient-to-r from-gray-800/50 to-gray-700/30 rounded-xl border border-gray-600/30 hover:border-gray-500/50 transition-all duration-200">
                            {product.imageUrl ? (
                              <img
                                src={getProductImageUrl(product.imageUrl)}
                                alt={product.name}
                                className="w-16 h-16 object-cover rounded-xl shadow-md"
                              />
                            ) : (
                              <div className="w-16 h-16 bg-gradient-to-r from-[#a15507] to-[#924e06] rounded-xl flex items-center justify-center">
                                <Coffee className="w-8 h-8 text-white" />
                              </div>
                            )}
                            <div className="flex-1">
                              <h4 className="text-white font-semibold text-lg">{product.name}</h4>
                              {product.description && (
                                <p className="text-gray-400 text-sm mt-1">{product.description}</p>
                              )}
                            </div>
                            <div className="text-[#a15507] font-bold text-xl">
                              ${product.price?.toFixed(2) || '0.00'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-gray-700 to-gray-600 flex items-center justify-center">
                    <Menu className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-300 mb-2">{t('noMenuItems')}</h3>
                  <p className="text-gray-500">{t('noMenuItemsDescription')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Photo Lightbox */}
      <PhotoLightbox
        isOpen={showLightbox}
        onClose={handleLightboxClose}
        images={lightboxImages}
        currentIndex={currentImageIndex}
        onIndexChange={handleImageIndexChange}
      />
    </div>
  );
};

export default ShopProfile;
